import { useState, useMemo } from "react";
import type { PlanEvent } from "../../../../shared/types";
import {
  getWeekDays,
  getToday,
  addDays,
} from "../../../../shared/types/date-helpers";
import { expandRecurringEvents } from "../../../events/utils/expand-recurring";
import { computeFreeSlots } from "../../utils/compute-free-slots";
import { FreeSlotsWeekNav } from "./FreeSlotsWeekNav";
import { FreeSlotsHeader } from "./FreeSlotsHeader";
import { FreeSlotsDay } from "./FreeSlotsDay";

interface FreeSlotsViewProps {
  readonly events: PlanEvent[];
}

export function FreeSlotsView({ events }: FreeSlotsViewProps) {
  const [weekReference, setWeekReference] = useState<string>(getToday());

  const weekDays = useMemo(() => getWeekDays(weekReference), [weekReference]);

  const expanded = useMemo(() => {
    const weekStart = weekDays[0]!;
    const weekEnd = weekDays[6]!;
    return expandRecurringEvents(events, weekStart, weekEnd);
  }, [events, weekDays]);

  const slotsByDay = useMemo(
    () =>
      weekDays.map((day) => ({
        date: day,
        slots: computeFreeSlots(expanded, day),
      })),
    [expanded, weekDays],
  );

  const totalFreeMinutes = useMemo(
    () =>
      slotsByDay.reduce(
        (acc, { slots }) =>
          acc + slots.reduce((s, slot) => s + slot.durationMinutes, 0),
        0,
      ),
    [slotsByDay],
  );

  const firstDay = weekDays[0]!;
  const lastDay = weekDays[6]!;

  return (
    <div className="flex flex-col gap-4 pt-6">
      <FreeSlotsWeekNav
        firstDay={firstDay}
        lastDay={lastDay}
        onPrevWeek={() => setWeekReference((ref) => addDays(ref, -7))}
        onNextWeek={() => setWeekReference((ref) => addDays(ref, 7))}
      />

      <FreeSlotsHeader totalFreeMinutes={totalFreeMinutes} />

      {slotsByDay.map(({ date, slots }) => (
        <FreeSlotsDay key={date} date={date} slots={slots} />
      ))}
    </div>
  );
}
